import { useEffect, useRef, useState } from 'react'
import { Clock3, ExternalLink, FileText, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Lang from '../../../config/lang'
import {
  isApplicationCompleted,
  isApplicationRejected,
} from '../../../config/applicationProgress'
import { submitMyTechnicalTest, uploadMyTechnicalTestFile } from '../api'
import { formatLocalizedDate } from '../applicationMapper'
import ApplicationMetaSummary from './ApplicationMetaSummary'
import ApplicationNotice from './ApplicationNotice'
import ApplicationProgressTimeline from './ApplicationProgressTimeline'

const ApplicationOverviewCard = ({
  job,
  lang,
  allowActions = true,
  onRefresh,
}) => {
  const navigate = useNavigate()
  const technicalTestFileInputRef = useRef(null)
  const technicalTestDragDepthRef = useRef(0)
  const [technicalTestFile, setTechnicalTestFile] = useState(null)
  const [technicalTestFilePreviewUrl, setTechnicalTestFilePreviewUrl] =
    useState('')
  const [technicalTestFileError, setTechnicalTestFileError] = useState('')
  const [isTechnicalTestDragging, setIsTechnicalTestDragging] = useState(false)
  const [isSubmittingTechnicalTest, setIsSubmittingTechnicalTest] =
    useState(false)
  const userToken = localStorage?.getItem('authToken') || ''
  const displayStatus = job?.displayStatus || job?.status || ''
  const psychotestLink = String(job?.psychotestLink || '').trim()
  const assessmentTestLink = String(job?.assessmentTestLink || '').trim()
  const candidateID = Number(job?.candidateID) || 0
  const pretestToken = String(job?.pretestToken || '').trim()
  const pretestLink = pretestToken
    ? `/${lang}/assessment?token=${encodeURIComponent(pretestToken)}`
    : ''
  const pretestExpiredAt = job?.pretestExpiredAt || ''
  const pretestExpiredAtTime = pretestExpiredAt
    ? new Date(pretestExpiredAt).getTime()
    : Number.NaN
  const assessmentDate = job?.assessmentDate || ''
  const assessmentDateTime = assessmentDate
    ? new Date(assessmentDate).getTime()
    : Number.NaN
  const isPretestExpired =
    job?.hasPretest === true &&
    Number.isFinite(pretestExpiredAtTime) &&
    pretestExpiredAtTime < Date.now()
  const bookingLabelKey =
    job?.interviewInvitationStage === 'USER'
      ? 'book_interview_user'
      : 'book_interview_hc'
  const isRejectedApplication = isApplicationRejected(job)
  const isCompletedApplication = isApplicationCompleted(job)
  const isTerminalApplication = isRejectedApplication || isCompletedApplication
  const rejectRemarks = String(job?.rejectRemarks || '').trim() || '-'
  const isInterviewStage =
    displayStatus === 'Interview HC' || displayStatus === 'Interview User'
  const activeInterviewStage =
    job?.interviewInvitationStage === 'USER' || displayStatus === 'Interview User'
      ? 'USER'
      : 'HC'
  const hasScheduledInterview = Boolean(
    activeInterviewStage === 'USER'
      ? job?.interviewUserDate
      : job?.interviewDate,
  )
  const invitationStatus = String(job?.interviewInvitationStatus || '')
    .trim()
    .toLowerCase()
  const invitationEmailStatus = String(
    job?.interviewInvitationEmailStatus || '',
  )
    .trim()
    .toLowerCase()
  const isWaitingInterviewInvitationEmail =
    invitationStatus === 'available' && invitationEmailStatus === 'pending'
  const interviewInvitationExpiredAt =
    job?.interviewInvitationExpiredAt || job?.bookingMeta?.expiredAt || ''
  const interviewInvitationExpiredAtTime = interviewInvitationExpiredAt
    ? new Date(interviewInvitationExpiredAt).getTime()
    : Number.NaN
  const showPretestAction =
    !isTerminalApplication &&
    displayStatus === 'Screening' &&
    candidateID > 0 &&
    pretestToken !== '' &&
    job?.isPassHardFilter === true &&
    job?.hasPretest === true &&
    !isPretestExpired &&
    (job?.isPassPretest === null || job?.isPassPretest === undefined)
  const showPretestExpiredNotice =
    !isCompletedApplication &&
    displayStatus === 'Screening' &&
    candidateID > 0 &&
    job?.isPassHardFilter === true &&
    job?.hasPretest === true &&
    isPretestExpired &&
    (job?.isPassPretest === null || job?.isPassPretest === undefined)
  const showWaitingJobOfferNotice =
    !isTerminalApplication &&
    displayStatus === 'Job Offer' &&
    job?.isOffer !== true &&
    job?.isOffer !== false &&
    job?.jobOffer?.isAvailable === false
  const showMedicalCheckUpNotice =
    !isTerminalApplication && displayStatus === 'Medical Check Up'
  const showInterviewBookingAction =
    !isTerminalApplication &&
    isInterviewStage &&
    !hasScheduledInterview &&
    invitationStatus === 'available' &&
    !isWaitingInterviewInvitationEmail &&
    job?.hasActiveInterviewInvitation &&
    job?.interviewInvitationID > 0
  const showExpiredInvitationNotice =
    !isCompletedApplication &&
    isInterviewStage &&
    !hasScheduledInterview &&
    invitationStatus === 'expired' &&
    !job?.interviewInvitationIsReschedule &&
    job?.interviewInvitationID > 0 &&
    Number.isFinite(interviewInvitationExpiredAtTime) &&
    interviewInvitationExpiredAtTime < Date.now()
  const showWaitingInvitationNotice =
    !isTerminalApplication &&
    isInterviewStage &&
    !hasScheduledInterview &&
    (isWaitingInterviewInvitationEmail ||
      (!job?.hasActiveInterviewInvitation &&
        !showExpiredInvitationNotice &&
        (!job?.interviewInvitationID || job?.interviewInvitationID <= 0)))
  const showPretestDeadlineNotice = showPretestAction && allowActions
  const showAssessmentDeadlineNotice =
    !isTerminalApplication &&
    displayStatus === 'Assessment' &&
    allowActions &&
    Number.isFinite(assessmentDateTime) &&
    assessmentDateTime >= Date.now()
  const showPsychotestCard =
    displayStatus === 'Assessment' && allowActions && psychotestLink !== ''
  const showTechnicalTestCard =
    displayStatus === 'Assessment' && allowActions && assessmentTestLink !== ''
  const technicalTestFileName = String(
    technicalTestFile?.name || '',
  ).toLowerCase()
  const technicalTestFileType = String(
    technicalTestFile?.type || '',
  ).toLowerCase()
  const isTechnicalTestPdf =
    technicalTestFile != null &&
    (technicalTestFileType.includes('pdf') ||
      technicalTestFileName.endsWith('.pdf'))
  let submittedTechnicalTestPath = ''

  try {
    const parsedAssessmentResult = JSON.parse(
      String(job?.assessmentResult || '{}'),
    )
    submittedTechnicalTestPath = String(
      parsedAssessmentResult?.uploadTechnical || '',
    ).trim()
  } catch (error) {
    submittedTechnicalTestPath = ''
  }

  const hasSubmittedTechnicalTest = submittedTechnicalTestPath !== ''
  const technicalTestUploadBoxClassName = isTechnicalTestDragging
    ? 'border-[#EA2427] bg-[#FFF5F5]'
    : technicalTestFileError
      ? 'border-[#EA2427] bg-[#FFF8F8]'
      : 'border-[#D1D5DB] bg-white'

  useEffect(() => {
    if (!isTechnicalTestPdf || technicalTestFile == null) {
      setTechnicalTestFilePreviewUrl('')
      return
    }

    const objectUrl = URL.createObjectURL(technicalTestFile)
    setTechnicalTestFilePreviewUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [isTechnicalTestPdf, technicalTestFile])

  const handleTechnicalTestFileSelected = (selectedFile) => {
    if (hasSubmittedTechnicalTest || isSubmittingTechnicalTest) {
      return
    }

    if (selectedFile == null) {
      return
    }

    const selectedFileName = String(selectedFile.name || '').toLowerCase()
    const selectedFileType = String(selectedFile.type || '').toLowerCase()
    const isPdfFile =
      selectedFileType.includes('pdf') || selectedFileName.endsWith('.pdf')
    const isZipFile =
      selectedFileType.includes('zip') || selectedFileName.endsWith('.zip')

    if (!isPdfFile && !isZipFile) {
      setTechnicalTestFile(null)
      setTechnicalTestFileError('File harus PDF atau ZIP.')
      return
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      setTechnicalTestFile(null)
      setTechnicalTestFileError('Ukuran file maksimal 2 MB.')
      return
    }

    setTechnicalTestFileError('')
    setTechnicalTestFile(selectedFile)
  }

  const handleClearTechnicalTestFile = () => {
    if (isSubmittingTechnicalTest) {
      return
    }

    setTechnicalTestFile(null)
    setTechnicalTestFilePreviewUrl('')
    setTechnicalTestFileError('')

    if (technicalTestFileInputRef.current) {
      technicalTestFileInputRef.current.value = ''
    }
  }

  const handleTechnicalTestDragEnter = (event) => {
    event.preventDefault()

    if (hasSubmittedTechnicalTest || isSubmittingTechnicalTest) {
      return
    }

    technicalTestDragDepthRef.current += 1
    setIsTechnicalTestDragging(true)
  }

  const handleTechnicalTestDragOver = (event) => {
    event.preventDefault()
  }

  const handleTechnicalTestDragLeave = (event) => {
    event.preventDefault()
    technicalTestDragDepthRef.current = Math.max(
      technicalTestDragDepthRef.current - 1,
      0,
    )

    if (technicalTestDragDepthRef.current === 0) {
      setIsTechnicalTestDragging(false)
    }
  }

  const handleTechnicalTestDrop = (event) => {
    event.preventDefault()
    technicalTestDragDepthRef.current = 0
    setIsTechnicalTestDragging(false)

    if (hasSubmittedTechnicalTest || isSubmittingTechnicalTest) {
      return
    }

    handleTechnicalTestFileSelected(event.dataTransfer.files?.[0])
  }

  const topNotices = [
    isRejectedApplication ? (
      <ApplicationNotice
        key='reject-remarks-notice'
        icon={<span className='text-[12px] font-bold leading-none'>!</span>}
        tone='danger'
        paddingClassName='px-4 py-3'
        contentClassName='items-start'
      >
        <div>
          <p className='font-semibold'>{Lang(lang, 'reject_remarks')}</p>
          <p className='mt-1'>{rejectRemarks}</p>
        </div>
      </ApplicationNotice>
    ) : null,
    showPretestDeadlineNotice ? (
      <ApplicationNotice
        key='pretest-deadline-notice'
        variant='countdown'
        countdownLabel={Lang(lang, 'pre_assessment_expires_in')}
        countdownTargetDate={pretestExpiredAt}
        countdownExpiryPrefix={Lang(lang, 'expires')}
        countdownExpiryText={formatLocalizedDate(pretestExpiredAt, lang, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      />
    ) : null,
    showPretestExpiredNotice ? (
      <ApplicationNotice
        key='pretest-expired-notice'
        icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
        tone='danger'
      >
        <p>{Lang(lang, 'pretest_expired_desc')}</p>
      </ApplicationNotice>
    ) : null,
    showAssessmentDeadlineNotice ? (
      <ApplicationNotice
        key='assessment-deadline-notice'
        variant='countdown'
        countdownLabel={Lang(lang, 'assessment_expires_in')}
        countdownTargetDate={assessmentDate}
        countdownExpiryPrefix={Lang(lang, 'expires')}
        countdownExpiryText={formatLocalizedDate(assessmentDate, lang, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      />
    ) : null,
    showWaitingJobOfferNotice ? (
      <ApplicationNotice
        key='waiting-job-offer-notice'
        icon={<span className='text-[12px] font-bold leading-none'>!</span>}
        tone='warning'
      >
        <p>{Lang(lang, 'job_offer_waiting_desc')}</p>
      </ApplicationNotice>
    ) : null,
    showMedicalCheckUpNotice ? (
      <ApplicationNotice
        key='medical-check-up-notice'
        icon={<span className='text-[12px] font-bold leading-none'>!</span>}
        tone='brand'
        paddingClassName='px-4 py-2'
        contentClassName='items-start'
      >
        <p>
          {Lang(lang, 'medical_check_up_notice_prefix')}
          <span className='font-semibold'>
            {Lang(lang, 'medical_check_up_notice_email_bold')}
          </span>
          {Lang(lang, 'medical_check_up_notice_middle')}
          <span className='font-semibold'>
            {Lang(lang, 'medical_check_up_notice_spam_bold')}
          </span>
          {Lang(lang, 'medical_check_up_notice_suffix')}
        </p>
      </ApplicationNotice>
    ) : null,
    showInterviewBookingAction && allowActions ? (
      <ApplicationNotice
        key='interview-booking-action'
        icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
        tone='warning'
        action={
          <button
            type='button'
            onClick={() =>
              navigate(
                `/${lang}/my-application?InterviewInvitationID=${job.interviewInvitationID}`,
              )
            }
            className='w-full rounded-full bg-[#EA2427] px-4 py-2 text-sm font-semibold text-white sm:w-auto'
          >
            {Lang(lang, bookingLabelKey)}
          </button>
        }
      >
        <p>{Lang(lang, 'interview_schedule_available_desc')}</p>
      </ApplicationNotice>
    ) : null,
    showExpiredInvitationNotice ? (
      <ApplicationNotice
        key='expired-invitation-notice'
        icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
        tone='danger'
      >
        <p>{Lang(lang, 'interview_invitation_expired_desc')}</p>
      </ApplicationNotice>
    ) : null,
    showWaitingInvitationNotice ? (
      <ApplicationNotice
        key='waiting-invitation-notice'
        icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
        tone='info'
      >
        <p>{Lang(lang, 'interview_invitation_waiting_desc')}</p>
      </ApplicationNotice>
    ) : null,
  ].filter(Boolean)

  return (
    <div className='rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-[25px]'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
        <div className='min-w-0'>
          <h3 className='break-words text-[20px] font-medium leading-[28px] text-[#111827]'>
            {job.title}
          </h3>
          <div className='mt-3 flex flex-wrap items-center'>
            <img
              src='/asset/icon/company.svg'
              alt='company'
              className='h-4 w-4'
            />
            <p className='ml-2 break-words text-[16px] font-normal leading-5 text-[#4B5563] uppercase'>
              {job.company}
            </p>
          </div>
        </div>
        <span className={`${job.badgeClassName} self-start`}>
          {job.badgeLabel}
        </span>
      </div>

      {topNotices.length > 0 ? (
        <div className='mt-8 flex flex-col gap-4'>{topNotices}</div>
      ) : null}

      <div className='mt-8'>
        <ApplicationMetaSummary
          dateApplied={job.dateApplied}
          location={job.location}
          lang={lang}
        />
      </div>

      <div className='mt-8'>
        <ApplicationProgressTimeline
          items={job.progressSteps}
          lang={lang}
          showBottomBorder={false}
        />
      </div>

      {showPretestAction && allowActions ? (
        <div className='mt-8 rounded-[10px] border border-[#E5E7EB] bg-white p-[14px]'>
          <div className='flex flex-col gap-2'>
            <p className='text-[14px] font-medium leading-5 text-[#111827]'>
              {Lang(lang, 'pretest_card_title')}
            </p>
            <p className='text-[12px] font-normal leading-4 text-[#6A7282]'>
              {Lang(lang, 'pretest_card_desc')}
            </p>
          </div>

          <div className='mt-4 flex justify-center'>
            <button
              type='button'
              onClick={() =>
                window.open(pretestLink, '_blank', 'noopener,noreferrer')
              }
              className='inline-flex h-[44px] w-[221px] items-center justify-center rounded-[34px] bg-[#EA2427] px-[32px] py-[10px] text-center text-[18px] font-bold leading-[24px] text-white backdrop-blur-[20px] transition hover:bg-[#D91F22]'
            >
              {Lang(lang, 'open_assessment')}
            </button>
          </div>
        </div>
      ) : null}

      {showPsychotestCard || showTechnicalTestCard ? (
        <div className='mt-8 flex flex-col gap-4'>
          {showPsychotestCard ? (
            <div className='rounded-[10px] border border-[#E5E7EB] bg-white p-[14px]'>
              <div className='flex flex-col gap-2'>
                <p className='text-[14px] font-medium leading-[20px] text-[#111827]'>
                  Psikotes Online
                </p>
                <p className='text-[12px] font-normal leading-[16px] text-[#6A7282]'>
                  Answer each question honestly and trust your instincts.
                </p>
              </div>

              <div className='mt-4 flex justify-center'>
                <button
                  type='button'
                  onClick={() =>
                    window.open(psychotestLink, '_blank', 'noopener,noreferrer')
                  }
                  className='inline-flex h-[44px] w-[221px] items-center justify-center rounded-[34px] bg-[#EA2427] px-[32px] py-[10px] text-center text-[18px] font-bold leading-[24px] text-white backdrop-blur-[20px] transition hover:bg-[#D91F22]'
                >
                  Open Assessment
                </button>
              </div>
            </div>
          ) : null}

          {showTechnicalTestCard ? (
            <div className='rounded-[10px] border border-[#E5E7EB] bg-white p-[14px]'>
              <p className='text-[14px] font-medium leading-[20px] text-[#111827]'>
                Technical Test
              </p>
              <p className='text-[12px] font-normal leading-[16px] text-[#6A7282]'>
                {hasSubmittedTechnicalTest
                  ? 'Your technical test answer has been submitted.'
                  : 'Walk us through your approach for each problem.'}
              </p>

              <div className='mt-4 rounded-[12px] bg-[#F3F4F6] px-6 py-5'>
                <div className='flex items-start gap-4'>
                  <div className='flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full bg-[#EA2427] text-[11px] font-normal leading-none text-white'>
                    <span className='block leading-none'>1</span>
                  </div>
                  <div className='flex flex-col'>
                    <p className='text-[13px] font-medium leading-[19.5px] text-[#101828]'>
                      Download the test first
                    </p>
                    <p className='mt-2 text-[12px] font-normal leading-[16px] text-[#6A7282]'>
                      Read through the questions before submitting your answer.
                    </p>
                    <button
                      type='button'
                      onClick={() =>
                        window.open(
                          assessmentTestLink,
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                      className='mt-4 inline-flex h-[30px] w-fit min-w-[104px] items-center justify-center gap-[6px] whitespace-nowrap rounded-[8px] border border-[#FCD34D] bg-[#FEF3C7] px-[12px] py-[6px] text-[12px] font-medium leading-[16px] text-[#92400E]'
                    >
                      <ExternalLink
                        className='h-4 w-4 flex-none text-[#92400E]'
                        strokeWidth={2.2}
                      />
                      Open Brief
                    </button>
                  </div>
                </div>
              </div>

              <div className='mt-4 rounded-[12px] bg-[#F3F4F6] px-4 py-4 sm:px-6 sm:py-5'>
                <div className='flex items-start gap-4'>
                  <div className='flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full bg-[#EA2427] text-[11px] font-normal leading-none text-white'>
                    <span className='block leading-none'>2</span>
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='text-[13px] font-medium leading-[19.5px] text-[#101828]'>
                      Upload your answer file
                    </p>
                    <p className='mt-2 text-[12px] font-normal leading-[16px] text-[#6A7282]'>
                      Make sure your file is legible and clearly readable.
                    </p>
                  </div>
                </div>

                <input
                  ref={technicalTestFileInputRef}
                  type='file'
                  accept='.pdf,.zip,application/pdf,application/zip'
                  className='sr-only'
                  disabled={
                    hasSubmittedTechnicalTest || isSubmittingTechnicalTest
                  }
                  onChange={(event) =>
                    handleTechnicalTestFileSelected(event.target.files?.[0])
                  }
                />

                <div
                  onClick={() => {
                    if (
                      technicalTestFile == null &&
                      !hasSubmittedTechnicalTest &&
                      !isSubmittingTechnicalTest
                    ) {
                      technicalTestFileInputRef.current?.click()
                    }
                  }}
                  onKeyDown={(event) => {
                    if (
                      technicalTestFile == null &&
                      !hasSubmittedTechnicalTest &&
                      !isSubmittingTechnicalTest &&
                      (event.key === 'Enter' || event.key === ' ')
                    ) {
                      event.preventDefault()
                      technicalTestFileInputRef.current?.click()
                    }
                  }}
                  onDragEnter={handleTechnicalTestDragEnter}
                  onDragOver={handleTechnicalTestDragOver}
                  onDragLeave={handleTechnicalTestDragLeave}
                  onDrop={handleTechnicalTestDrop}
                  role='button'
                  tabIndex={0}
                  className={`mt-4 flex min-h-[120px] w-full items-center justify-center rounded-[10px] border-2 border-dashed px-4 py-6 text-center transition sm:min-h-[136px] ${technicalTestFile == null && !hasSubmittedTechnicalTest && !isSubmittingTechnicalTest ? 'cursor-pointer' : 'cursor-default'} ${technicalTestUploadBoxClassName}`}
                >
                  {hasSubmittedTechnicalTest ? (
                    <div className='w-full'>
                      <div className='flex items-center justify-between rounded-[14px] bg-[#DCFCE7] px-4 py-3'>
                        <div className='min-w-0 flex items-center gap-3'>
                          <FileText
                            className='h-5 w-5 flex-none text-[#16A34A]'
                            strokeWidth={2}
                          />
                          <p className='truncate text-[13px] font-medium leading-[20px] text-[#166534]'>
                            {submittedTechnicalTestPath}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : technicalTestFile != null ? (
                    <div className='w-full'>
                      {isTechnicalTestPdf && technicalTestFilePreviewUrl ? (
                        <div className='mx-auto w-full max-w-[420px] overflow-hidden rounded-[10px] border border-[#E5E7EB] bg-white'>
                          <iframe
                            title='technical-test-pdf-preview'
                            src={technicalTestFilePreviewUrl}
                            className='h-[240px] w-full'
                          />
                        </div>
                      ) : null}

                      <div className='mt-3 flex items-center justify-between rounded-[14px] bg-[#DCE7F8] px-4 py-3'>
                        <div className='min-w-0 flex items-center gap-3'>
                          <FileText
                            className='h-5 w-5 flex-none text-[#2563EB]'
                            strokeWidth={2}
                          />
                          <p className='truncate text-[13px] font-medium leading-[20px] text-[#2563EB]'>
                            {technicalTestFile?.name || '-'}
                          </p>
                        </div>
                        <button
                          type='button'
                          onClick={(event) => {
                            event.stopPropagation()
                            handleClearTechnicalTestFile()
                          }}
                          className='ml-3 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-[#6B7280] transition hover:bg-[#C9D8F3] hover:text-[#4B5563]'
                          aria-label='Remove file'
                        >
                          <X className='h-5 w-5' strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {technicalTestFile == null && !hasSubmittedTechnicalTest ? (
                    <div className='flex flex-col items-center'>
                      <img
                        src='/asset/icon/upload.svg'
                        alt='upload'
                        className='h-6 w-6'
                      />
                      <p className='mt-3 text-[13px] font-medium leading-[19.5px] text-[#111827]'>
                        Choose or drag a file here
                      </p>
                      <p className='mt-1 text-[12px] font-normal leading-[16px] text-[#6A7282]'>
                        PDF or ZIP, max. 2 MB
                      </p>
                    </div>
                  ) : null}
                </div>

                {technicalTestFileError ? (
                  <p className='mt-2 text-[12px] font-normal leading-[16px] text-[#EA2427]'>
                    {technicalTestFileError}
                  </p>
                ) : null}
              </div>

              <div className='mt-4 flex justify-center'>
                <button
                  type='button'
                  disabled={
                    hasSubmittedTechnicalTest || isSubmittingTechnicalTest
                  }
                  onClick={async () => {
                    if (hasSubmittedTechnicalTest) {
                      return
                    }

                    if (!userToken) {
                      setTechnicalTestFileError('Please login first.')
                      return
                    }

                    if (candidateID <= 0) {
                      setTechnicalTestFileError('Candidate data is not valid.')
                      return
                    }

                    if (technicalTestFile == null) {
                      setTechnicalTestFileError(
                        'Please choose your answer file first.',
                      )
                      return
                    }

                    setTechnicalTestFileError('')
                    setIsSubmittingTechnicalTest(true)

                    const uploadResponse = await uploadMyTechnicalTestFile({
                      file: technicalTestFile,
                      userToken,
                    })
                    const attachmentPath =
                      uploadResponse?.attachmentPath ||
                      uploadResponse?.AttachmentPath ||
                      ''

                    if (typeof uploadResponse === 'string' || !attachmentPath) {
                      setIsSubmittingTechnicalTest(false)
                      setTechnicalTestFileError(
                        typeof uploadResponse === 'string'
                          ? uploadResponse
                          : 'Failed to upload the selected file. Please try again.',
                      )
                      return
                    }

                    const submitResponse = await submitMyTechnicalTest({
                      candidateID,
                      uploadTechnical: attachmentPath,
                      userToken,
                    })

                    setIsSubmittingTechnicalTest(false)

                    if (typeof submitResponse === 'string') {
                      setTechnicalTestFileError(submitResponse)
                      return
                    }

                    Swal.fire({
                      title: 'Success!',
                      text:
                        submitResponse?.message ||
                        'Technical test answer submitted successfully.',
                      icon: 'success',
                      timer: 1800,
                      showConfirmButton: false,
                      customClass: { popup: 'my-custom-popup' },
                    })

                    handleClearTechnicalTestFile()

                    if (typeof onRefresh === 'function') {
                      await onRefresh()
                    }
                  }}
                  className='inline-flex h-[44px] w-full items-center justify-center rounded-[34px] bg-[#EA2427] px-[32px] py-[10px] text-center text-[18px] font-bold leading-[24px] text-white backdrop-blur-[20px] transition hover:bg-[#D91F22] disabled:cursor-not-allowed disabled:bg-[#FCA5A5] sm:w-[221px]'
                >
                  {isSubmittingTechnicalTest
                    ? Lang(lang, 'loading')
                    : hasSubmittedTechnicalTest
                      ? 'Submitted'
                      : 'Submit Answer'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default ApplicationOverviewCard
